import 'rc-steps/assets/index.css'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { validateEmail } from 'utils/email-validator'

import { PrimaryButton } from '../../../components/buttons/Buttons'
import Field from '../../../components/input/InputField'
import DropDownSelector from '../../../components/selector/DropDownSelector'
import PhoneCodeSelector, {
  getDialCode,
} from '../../../components/selector/PhoneCodeSelector'
import { mediaQueries, palette } from '../../../styles/variables'
import { validatePhone } from '../../../utils/inputValidation'
import { Desktop, Mobile } from '../../../utils/media'
import { translate } from '../../../utils/translation'

import {
  budgetOptions,
  businessTypesOptions,
  challengesOptions,
  industryOptions,
  marketingTeamSizeOptions,
  numberOfEmployeesOptions,
  urgencyOptions,
  shouldShowChallenges,
} from './const'
import { NavButton, NavButtons, StepContent, StepWrapper } from './styled'

const Fields = styled.div(({ fieldCount = 2 }) => ({
  display: 'flex',
  gap: '1em',
  width: '100%',
  minWidth: 0,

  ...(fieldCount === 3 && {
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      gap: '0',
      '& > *': {
        width: '100%',
      },
    },
  }),

  '& > div': {
    minWidth: 0,
    flex: 1,
  },

  [mediaQueries.smallMobile]: {
    flexDirection: 'column',
    gap: 0,
  },
}))

const PhoneField = styled.div({
  flex: 9,
  [mediaQueries.smallMobile]: {
    marginLeft: 0,
  },
})

const InvalidMessage = styled.span({
  left: 0,
  top: 0,
  color: palette.red,
  fontSize: 10,
  display: 'block',
  marginBottom: '5px',
})

const activeStyle = {
  borderColor: palette.orange,
  backgroundColor: palette.orange,
  color: palette.white,
}

const inactiveStyle = {
  borderColor: palette.lightGray70,
  color: palette.darkGray50,
}

const Step6 = ({
  user,
  growYourTraffic,
  onSubmit,
  handleChangeValue,
  trackWantNPDTeamGrowTrafficClick,
  trackNPDTeamGrowTrafficLeadSuccess,
}) => {
  const [isProcessing, setIsProcessing] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      countryCode: user.countryCode,
      phoneNumber: '',
      website: '',
      industry: '',
      budget: '',
      challenges: '',
      urgency: '',
      businessTypes: '',
      numberOfEmployees: '',
      teamSize: '',
    },
  })

  const translateOptions = (options) => {
    return options.map((option) => {
      const label = option?.variable
        ? translate(option.label, option.variable)
        : translate(option.label)
      return {
        label,
        value: option.value,
      }
    })
  }

  const handleChangeDropdown = (property) => (value) => {
    const clearChallenge =
      property === 'budget'
        ? {
            challenges: '',
            urgency: '',
            businessTypes: '',
            numberOfEmployees: '',
          }
        : {}

    Object.entries(clearChallenge).forEach(([key]) => {
      setValue(key, '')
    })
    setValue(property, value)
    trigger(property)
  }

  const allowOnlyNumbers = (event) => {
    const { value } = event.target
    const isNumber = value.replace(/\D/g, '')
    clearErrors('phoneNumber')
    setValue('phoneNumber', isNumber)
  }

  const onValidSubmit = (data) => {
    if (!growYourTraffic) {
      onSubmit(null)
      return
    }

    const phoneCode = getDialCode(data.countryCode)
    const rawPhone = `${phoneCode || ''}${data.phoneNumber || ''}`

    const leadsData = {
      email: data.email,
      phone: rawPhone,
      firstname: data.firstName,
      lastname: data.lastName,
      domain: data.website,
      industry: data.industry,
      budget: data.budget,
      challenges: data.challenges,
      urgency: data.urgency,
      business_types: data.businessTypes,
      employees: data.numberOfEmployees,
      country: data.countryCode.toLowerCase(),
      team_size: data.teamSize,
    }

    setIsProcessing(true)

    trackNPDTeamGrowTrafficLeadSuccess({
      businessTypes: data.businessTypes,
      challenges: data.challenges,
      numberOfEmployees: data.numberOfEmployees,
      urgency: data.urgency,
      industry: data.industry,
      budget: data.budget,
    })

    onSubmit(leadsData)
  }

  return (
    <StepWrapper width="100%" className="tw-items-center">
      <StepContent className="tw-max-w-[680px] xl:tw-mx-auto md:tw-w-full tw-w-auto">
        <NavButtons
          style={{
            flexDirection: 'row',
            marginBottom: '20px',
            borderTop: 'none',
          }}
        >
          <Desktop>
            <PrimaryButton
              testId="primary-button-step6-noaff"
              onClick={() => {
                setIsProcessing(true)
                onSubmit(null)
              }}
              tag={translate('no_aff')}
              style={{
                ...inactiveStyle,
                maxWidth: '150px',
              }}
              isLoading={isProcessing}
            />
          </Desktop>
          <Mobile>
            <PrimaryButton
              onClick={() => {
                setIsProcessing(true)
                onSubmit(null)
              }}
              tag={translate('no_aff')}
              style={{
                ...inactiveStyle,
                maxWidth: '150px',
              }}
              isLoading={isProcessing}
            />
          </Mobile>
          <PrimaryButton
            testId="primary-button-step6-yes"
            onClick={() => {
              handleChangeValue('growYourTraffic')(true)
              trackWantNPDTeamGrowTrafficClick()
            }}
            style={
              growYourTraffic
                ? {
                    ...activeStyle,
                    maxWidth: '150px',
                  }
                : {
                    maxWidth: '150px',
                  }
            }
            tag={translate('yes')}
          />
        </NavButtons>

        {growYourTraffic && (
          <form onSubmit={handleSubmit(onValidSubmit)}>
            <Fields fieldCount={3}>
              <Field
                {...register('email', {
                  required: true,
                  validate: validateEmail,
                })}
                placeholder={translate('email')}
                data-testid="emailForm"
                isValid={!errors.email}
                invalidMessage={translate('invalid_email')}
                invalidMessagePlace="left"
                styles={{ flex: 1 }}
              />
              <Fields style={{ flex: 1 }}>
                <PhoneCodeSelector
                  placeholder={translate('country_code')}
                  value={watch('countryCode')}
                  width={130}
                  onChange={handleChangeDropdown('countryCode')}
                />
                <PhoneField>
                  <Field
                    {...register('phoneNumber', {
                      required: true,
                      validate: validatePhone,
                    })}
                    data-testid="phoneForm"
                    placeholder={translate('phone_number')}
                    isValid={!errors.phoneNumber}
                    invalidMessage={translate('invalid_phone')}
                    invalidMessagePlace="left"
                    onChange={allowOnlyNumbers}
                  />
                </PhoneField>
              </Fields>
            </Fields>
            <Fields>
              <Field
                {...register('firstName', { required: true })}
                data-testid="firstNameForm"
                placeholder={translate('first_name')}
                isValid={!errors.firstName}
                invalidMessage={translate('required_field_warning')}
                invalidMessagePlace="left"
                styles={{ flex: 1 }}
              />
              <Field
                {...register('lastName', { required: true })}
                data-testid="lastNameForm"
                placeholder={translate('last_name')}
                isValid={!errors.lastName}
                invalidMessage={translate('required_field_warning')}
                invalidMessagePlace="left"
                styles={{ flex: 1 }}
              />
            </Fields>
            <Fields>
              <Field
                {...register('website', { required: true })}
                data-testid="websiteURLForm"
                placeholder={translate('placeholder_website_url')}
                isValid={!errors.website}
                invalidMessage={translate('required_field_warning')}
                invalidMessagePlace="left"
                styles={{ flex: 1 }}
              />
              <div style={{ flex: 1 }}>
                <DropDownSelector
                  testId="step6-drop-down-selector-budget"
                  data-tip
                  data-for="invalid-budget"
                  name="budget"
                  options={translateOptions(budgetOptions)}
                  placeholder={translate('monthly_marketing_budget')}
                  value={watch('budget') || ''}
                  onChange={handleChangeDropdown('budget')}
                  arrowColor={palette.darkGray}
                  borderColor={palette.gray50}
                />
                {errors.budget && (
                  <InvalidMessage>
                    {translate('required_field_warning')}
                  </InvalidMessage>
                )}
              </div>
            </Fields>
            <Fields>
              <DropDownSelector
                testId="step6-drop-down-selector-team-size"
                data-tip
                data-for="invalid-team-size"
                name="teamSize"
                options={translateOptions(marketingTeamSizeOptions)}
                placeholder={translate('marketing_team_size')}
                value={watch('teamSize') || ''}
                onChange={handleChangeDropdown('teamSize')}
                arrowColor={palette.darkGray}
                borderColor={palette.gray50}
              />
              {errors.teamSize && (
                <InvalidMessage>
                  {translate('required_field_warning')}
                </InvalidMessage>
              )}
              <div style={{ flex: 1 }}>
                <DropDownSelector
                  testId="step6-drop-down-selector"
                  data-tip
                  data-for="invalid-business-type"
                  name="businessTypes"
                  options={translateOptions(businessTypesOptions)}
                  placeholder={translate('business_types')}
                  value={watch('businessTypes') || ''}
                  onChange={handleChangeDropdown('businessTypes')}
                  arrowColor={palette.darkGray}
                  borderColor={palette.gray50}
                />
                {errors.businessTypes && (
                  <InvalidMessage>
                    {translate('required_field_warning')}
                  </InvalidMessage>
                )}
              </div>
            </Fields>
            <DropDownSelector
              testId="step6-drop-down-selector-industry"
              data-tip
              data-for="invalid-industry"
              name="industry"
              options={translateOptions(industryOptions)}
              placeholder={translate('industry')}
              value={watch('industry') || ''}
              onChange={handleChangeDropdown('industry')}
              arrowColor={palette.darkGray}
              borderColor={palette.gray50}
            />
            {errors.industry && (
              <InvalidMessage>
                {translate('required_field_warning')}
              </InvalidMessage>
            )}
            {shouldShowChallenges(watch('budget')) && (
              <>
                <Fields>
                  <DropDownSelector
                    testId="step6-drop-down-selector"
                    data-tip
                    data-for="invalid-challenges"
                    name="challenges"
                    options={translateOptions(challengesOptions)}
                    placeholder={translate('challenges')}
                    value={watch('challenges') || ''}
                    onChange={handleChangeDropdown('challenges')}
                    arrowColor={palette.darkGray}
                    borderColor={palette.gray50}
                  />
                  {errors.challenges && (
                    <InvalidMessage>
                      {translate('required_field_warning')}
                    </InvalidMessage>
                  )}
                  <div style={{ flex: 1 }}>
                    <DropDownSelector
                      testId="step6-drop-down-selector"
                      data-tip
                      data-for="invalid-number-of-employees"
                      name="numberOfEmployees"
                      options={translateOptions(numberOfEmployeesOptions)}
                      placeholder={translate('employees_c')}
                      value={watch('numberOfEmployees') || ''}
                      onChange={handleChangeDropdown('numberOfEmployees')}
                      arrowColor={palette.darkGray}
                      borderColor={palette.gray50}
                    />
                    {errors.numberOfEmployees && (
                      <InvalidMessage>
                        {translate('required_field_warning')}
                      </InvalidMessage>
                    )}
                  </div>
                </Fields>
                <Fields>
                  <div style={{ flex: 1 }}>
                    <DropDownSelector
                      testId="step6-drop-down-selector"
                      data-tip
                      data-for="invalid-urgency"
                      name="urgency"
                      options={translateOptions(urgencyOptions)}
                      placeholder={translate('your_urgency_for_hiring_help')}
                      value={watch('urgency') || ''}
                      onChange={handleChangeDropdown('urgency')}
                      arrowColor={palette.darkGray}
                      borderColor={palette.gray50}
                    />
                    {errors.urgency && (
                      <InvalidMessage>
                        {translate('required_field_warning')}
                      </InvalidMessage>
                    )}
                  </div>
                </Fields>
              </>
            )}
            <NavButtons>
              <NavButton
                testId="add-project-step6-finish"
                color="orange"
                type="submit"
                tag={translate('finish_button')}
                isLoading={isProcessing}
              />
            </NavButtons>
          </form>
        )}
      </StepContent>
    </StepWrapper>
  )
}

export default Step6
